import React, { useEffect, useState } from 'react';
import { CustomInputBox } from 'Components/Input/Input';
import axiosInstance from 'Utils/Interceptor';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import CustomModal from 'Components/Modal/CustomModal';
import ThankYouModalForCallBack from 'Pages/Home/Components/ThankYouModal';
import SelectBox from 'Components/Select/Select';
import { useSelector } from 'react-redux';
import UploadPdfFile from './UploadPdfFile';
import { NUMBER_REGEX } from 'Utils/Constant';
import { NameInput } from 'Components/NameInput/NameInput';
import { NumberInput } from 'Components/NumberInput/NumberInput';
import { useNavigate } from 'react-router-dom';

const validationSchemaForEditProfileModal = Yup.object({
  first_name: Yup.string()
    .required('First Name is required')
    .matches(/^[a-zA-Z ]+$/, 'Only alphabets are accepted'),
  last_name: Yup.string()
    .matches(/^[a-zA-Z ]+$/, 'Only alphabets are accepted'),
  mobileno: Yup.string()
    .required('Mobile Number is required')
    .matches(NUMBER_REGEX, 'Mobile Number must be a valid number'),
  query: Yup.object().nullable().required('Please select a city'),
  comment: Yup.string().nullable(),
  rx_file: Yup.string().required('Upload Prescription is required')
});

function HelpModal({ handleCloseModal, showModal }) {
  const {
    profile: { email, mobileno, last_name, first_name }
  } = useSelector((state) => state.login);

  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const handleCloseThankYouModal = () => setShowThankYouModal(false);
  const [queryList, setQueryList] = useState([]);
  const [fileUrl, setFileURL] = useState(null);
  const navigate = useNavigate();

  const fetchContent = async () => {
    try {
      const response = await axiosInstance.get(`/api/user/cities`);
      setQueryList(
        response?.data?.data.map((item) => ({
          value: item.city_id,
          label: item.city_name
        }))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      first_name: first_name,
      last_name: last_name,
      email: email,
      mobileno: mobileno,
      query: null,
      comment: '',
      rx_file: fileUrl
    },
    validationSchema: validationSchemaForEditProfileModal,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (formikValues, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const payload = {
          first_name: formikValues.first_name,
          last_name: formikValues.last_name,
          mobile_number: formikValues.mobileno,
          city_id: formikValues.query?.value,
          rx_file: formikValues.rx_file
        };
        const { data } = await axiosInstance.post(
          '/api/user/prescriptions',
          payload
        );
        if (data) {
          handleCloseModal();
          // setShowThankYouModal(true);
          navigate('/thank-you')
        }
      } catch (errors) {
        toast.error(errors?.response?.data?.error?.message, {});
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    fetchContent();
    return () => formik.resetForm();
  }, [showModal]);

  // Update setFileURL to also update the Formik state
  const updateFileUrl = (url) => {
    setFileURL(url);
    formik.setFieldValue('rx_file', url);
  };

  return (
    <div>
      <CustomModal
        onClose={handleCloseModal}
        show={showModal}
        size="full"
      >
        <div className="">
          <div className="get-a-callback-wrapper">
            <h2>Upload Prescription!</h2>
            <div className="form-group create-group">
              <NameInput
                placeholder="Enter First Name *"
                formik={formik}
                label={'first_name'}
              />
            </div>
            <div className="form-group create-group">
              <NameInput
                placeholder="Enter Last Name"
                formik={formik}
                label={'last_name'}
              />
            </div>
            <div className="form-group create-group">
              <NumberInput
                placeholder="Enter Mobile Number *"
                formik={formik}
                label={'mobileno'}
              />
            </div>
            <div className="form-group create-group">
              <SelectBox
                options={queryList}
                placeholder="Select City *"
                formik={formik}
                label="query"
              />
            </div>

            <div className="input-group">
              <UploadPdfFile setFileURL={updateFileUrl} />
              {formik.touched.rx_file && formik.errors.rx_file && (
                <p className="error-message">{formik.errors.rx_file}</p>
              )}
            </div>

            <div className="form-group submit-action">
              <button
                disabled={
                  !formik.isValid || formik.isSubmitting || !formik.dirty
                }
                type="submit"
                onClick={formik.handleSubmit}
                className="btn btn-md"
              >
                <span>Submit</span>
              </button>
              <button
                onClick={handleCloseModal}
                className="btn btn-md btn-stroke"
              >
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      </CustomModal>
      <ThankYouModalForCallBack
        show={showThankYouModal}
        handleCloseModal={handleCloseThankYouModal}
      />
    </div>
  );
}

function UploadPrescriptionModal({ children, open, handleClose }) {
  const [showGetACallBack, setShowGetACallBack] = useState(false);

  const handleCloseModal = () => {
    setShowGetACallBack(!showGetACallBack);
    handleClose && handleClose();
  };

  useEffect(() => {
    open && setShowGetACallBack(true);
  }, [open]);

  return (
    <>
      <div onClick={handleCloseModal}>{children}</div>
      <HelpModal
        showModal={showGetACallBack}
        handleCloseModal={handleCloseModal}
      />
    </>
  );
}

export default UploadPrescriptionModal;
